/**
 * Central image configuration for the wedding template.
 *
 * Every photo is referenced by a local path under `/public/images/`.
 * To personalize a wedding, simply replace the files inside that folder —
 * no code changes are required. The `fallback` field points to a remote
 * placeholder image that is shown automatically if the local file does not
 * exist yet, so the template always renders something beautiful.
 */

export interface WeddingImage {
  /** Local path to the image (relative to /public). */
  src: string;
  /** Optional remote placeholder shown if `src` cannot be loaded. */
  fallback?: string;
  /** Accessible alt text for the image. */
  alt: string;
}

export interface GalleryImage extends WeddingImage {
  orientation: 'portrait' | 'landscape';
}

export interface WeddingImages {
  /** Open Graph image used when sharing the link (WhatsApp, social media). */
  ogImage: string;
  /** Full-screen opening photograph. */
  hero: WeddingImage;
  /** Full-width divider image between information blocks. */
  photoDivider: WeddingImage;
  /** Full-width closing photograph. */
  finalPhoto: WeddingImage;
  /** Story section photographs (1 large + 2 small). */
  story: WeddingImage[];
  /** Gallery photographs (editorial mixed layout). */
  gallery: GalleryImage[];
}

export const weddingImages: WeddingImages = {
  ogImage: '/images/og-wedding.jpg',

  hero: {
    src: '/images/hero.jpg',
    fallback:
      'https://readdy.ai/api/search-image?query=Elegant%20romantic%20couple%20portrait%20in%20golden%20hour%20light%20with%20soft%20natural%20greenery%20background%2C%20editorial%20wedding%20photography%20style%2C%20warm%20ivory%20champagne%20tones%2C%20luxury%20wedding%20invitation%20aesthetic%2C%20minimal%20clean%20composition&width=1200&height=1600&seq=wedding-hero-01&orientation=portrait',
    alt: 'Bride and groom in a romantic golden-hour portrait',
  },

  photoDivider: {
    src: '/images/couple-01.jpg',
    fallback:
      'https://readdy.ai/api/search-image?query=Romantic%20couple%20walking%20through%20lush%20garden%20pathway%20in%20golden%20hour%20light%2C%20editorial%20wedding%20photography%20with%20warm%20neutral%20tones%2C%20soft%20bokeh%20background%2C%20ivory%20and%20champagne%20color%20palette&width=1600&height=900&seq=wedding-divider-01&orientation=landscape',
    alt: 'Couple walking through a garden',
  },

  finalPhoto: {
    src: '/images/couple-02.jpg',
    fallback:
      'https://readdy.ai/api/search-image?query=Emotional%20wedding%20moment%20couple%20embracing%20in%20soft%20warm%20light%2C%20editorial%20photography%20style%2C%20romantic%20intimate%20atmosphere%2C%20warm%20cream%20ivory%20tones%2C%20luxury%20wedding%20aesthetic&width=1600&height=900&seq=wedding-final-01&orientation=landscape',
    alt: 'Couple embracing in soft warm light',
  },

  story: [
    {
      src: '/images/story-01.jpg',
      fallback:
        'https://readdy.ai/api/search-image?query=Happy%20couple%20laughing%20together%20in%20beautiful%20outdoor%20setting%2C%20editorial%20wedding%20photography%20style%2C%20warm%20golden%20light%2C%20romantic%20joyful%20mood%2C%20ivory%20tones&width=1200&height=800&seq=wedding-story-01&orientation=landscape',
      alt: 'Couple laughing together',
    },
    {
      src: '/images/story-02.jpg',
      fallback:
        'https://readdy.ai/api/search-image?query=Couple%20sharing%20intimate%20moment%20looking%20at%20each%20other%20with%20warm%20soft%20light%2C%20editorial%20style%2C%20romantic%20atmosphere&width=600&height=800&seq=wedding-story-02&orientation=portrait',
      alt: 'Couple sharing an intimate moment',
    },
    {
      src: '/images/story-03.jpg',
      fallback:
        'https://readdy.ai/api/search-image?query=Couple%20holding%20hands%20close%20up%20detail%20shot%2C%20soft%20warm%20light%2C%20editorial%20wedding%20photography%20style%2C%20romantic%20intimate%20mood&width=600&height=800&seq=wedding-story-03&orientation=portrait',
      alt: 'Couple holding hands',
    },
  ],

  gallery: [
    {
      src: '/images/gallery-01.jpg',
      fallback:
        'https://readdy.ai/api/search-image?query=Elegant%20wedding%20ceremony%20venue%20decorated%20with%20white%20flowers%20and%20candles%2C%20warm%20golden%20light%2C%20editorial%20photography%20style%2C%20ivory%20and%20champagne%20tones&width=1200&height=800&seq=wedding-gallery-01&orientation=landscape',
      alt: 'Ceremony venue',
      orientation: 'landscape',
    },
    {
      src: '/images/gallery-02.jpg',
      fallback:
        'https://readdy.ai/api/search-image?query=Close%20up%20of%20elegant%20wedding%20bouquet%20with%20white%20roses%20and%20greenery%2C%20soft%20natural%20light%2C%20editorial%20photography%20style%2C%20warm%20neutral%20tones&width=800&height=1200&seq=wedding-gallery-02&orientation=portrait',
      alt: 'Wedding bouquet',
      orientation: 'portrait',
    },
    {
      src: '/images/gallery-03.jpg',
      fallback:
        'https://readdy.ai/api/search-image?query=Couple%20holding%20hands%20walking%20through%20beautiful%20garden%20at%20sunset%2C%20editorial%20wedding%20photography%2C%20warm%20golden%20hour%20light%2C%20soft%20romantic%20atmosphere&width=1200&height=800&seq=wedding-gallery-03&orientation=landscape',
      alt: 'Couple walking at sunset',
      orientation: 'landscape',
    },
    {
      src: '/images/gallery-04.jpg',
      fallback:
        'https://readdy.ai/api/search-image?query=Elegant%20couple%20portrait%20in%20formal%20attire%20against%20soft%20neutral%20background%2C%20editorial%20wedding%20photography%20style%2C%20warm%20ivory%20tones%2C%20romantic%20mood&width=800&height=1200&seq=wedding-gallery-04&orientation=portrait',
      alt: 'Couple portrait',
      orientation: 'portrait',
    },
    {
      src: '/images/gallery-05.jpg',
      fallback:
        'https://readdy.ai/api/search-image?query=Beautifully%20decorated%20wedding%20reception%20table%20with%20elegant%20floral%20centerpieces%20and%20candlelight%2C%20warm%20intimate%20lighting%2C%20editorial%20style%2C%20ivory%20and%20champagne%20tones&width=1200&height=800&seq=wedding-gallery-05&orientation=landscape',
      alt: 'Reception table',
      orientation: 'landscape',
    },
    {
      src: '/images/gallery-06.jpg',
      fallback:
        'https://readdy.ai/api/search-image?query=Elegant%20wedding%20rings%20on%20soft%20fabric%20with%20delicate%20flowers%2C%20macro%20photography%2C%20warm%20neutral%20tones%2C%20editorial%20style%2C%20romantic%20detail%20shot&width=800&height=1200&seq=wedding-gallery-06&orientation=portrait',
      alt: 'Wedding rings',
      orientation: 'portrait',
    },
    {
      src: '/images/gallery-07.jpg',
      fallback:
        'https://readdy.ai/api/search-image?query=Romantic%20couple%20silhouette%20against%20warm%20sunset%20sky%2C%20editorial%20wedding%20photography%20style%2C%20soft%20golden%20light%2C%20elegant%20minimal%20composition&width=1200&height=800&seq=wedding-gallery-07&orientation=landscape',
      alt: 'Sunset silhouette',
      orientation: 'landscape',
    },
    {
      src: '/images/gallery-08.jpg',
      fallback:
        'https://readdy.ai/api/search-image?query=Bride%20preparing%20for%20wedding%20day%20in%20elegant%20setting%20with%20soft%20natural%20light%2C%20editorial%20photography%20style%2C%20warm%20ivory%20and%20cream%20tones%2C%20romantic%20atmosphere&width=800&height=1200&seq=wedding-gallery-08&orientation=portrait',
      alt: 'Getting ready',
      orientation: 'portrait',
    },
  ],
};